import { DashboardCatalog, DashboardChannel, DashboardIntegrationHealth } from '../models/dashboard.model';
import { plural } from './dashboard-flow';

// Regras puras do widget Conexões (spec §10): estado de cada integração e a linha de anúncios e
// produtos. Sem vencimento de token: a renovação é automática.

export type ConnectionState = 'ok' | 'degraded' | 'down' | 'unknown';

/** O que o widget lê de cada integração: o canal do snapshot novo ou a integração do snapshot antigo. */
export interface ConnectionFacts {
  integrationId: number;
  name: string;
  status: string | null;
  statusMessage: string | null;
  pendingMessages: number;
  rateLimitedUntil: string | null;
}

export interface ConnectionLine {
  id: number;
  name: string;
  state: ConnectionState;
  /** "Ativa, sem fila" / "N na fila" / "Limite de chamadas até HH:mm" / "Inativa". */
  text: string;
  /** Mensagem da integração inativa. */
  message: string | null;
  /** "Reautorizar" só aparece com a integração inativa. */
  reauthorize: boolean;
}

export function connectionFacts(
  channels: DashboardChannel[] | null | undefined,
  integrations: DashboardIntegrationHealth[] | null | undefined,
): ConnectionFacts[] {
  if (channels) return channels;
  // API antiga: sem `channels`, a integração do snapshot antigo diz o mesmo (o rótulo é o provider).
  return (integrations ?? []).map((i) => ({
    integrationId: i.id,
    name: i.provider,
    status: i.status,
    statusMessage: i.statusMessage,
    pendingMessages: i.pendingMessages,
    rateLimitedUntil: i.rateLimitedUntil,
  }));
}

function rateLimitedAt(until: string | null, now: Date): Date | null {
  if (!until) return null;
  const at = new Date(until);
  return at.getTime() > now.getTime() ? at : null;
}

const hhmm = (d: Date): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export function connectionLine(c: ConnectionFacts, now: Date = new Date()): ConnectionLine {
  const base = { id: c.integrationId, name: c.name, message: null as string | null, reauthorize: false };

  // Sem a permissão `integration` a API não manda o status; o widget nem aparece nesse caso.
  if (c.status === null) return { ...base, state: 'unknown', text: '' };

  if (c.status !== 'Active') {
    return {
      ...base,
      state: 'down',
      text: c.status === 'Error' ? 'Com erro' : 'Inativa',
      message: c.statusMessage || null,
      reauthorize: true,
    };
  }

  const limit = rateLimitedAt(c.rateLimitedUntil, now);
  const queue = c.pendingMessages > 0 ? `${c.pendingMessages.toLocaleString('pt-BR')} na fila` : null;
  if (limit) {
    return { ...base, state: 'degraded', text: `Limite de chamadas até ${hhmm(limit)}${queue ? `, ${queue}` : ''}` };
  }
  if (queue) return { ...base, state: 'degraded', text: queue };
  return { ...base, state: 'ok', text: 'Ativa, sem fila' };
}

export interface CatalogLine {
  published: number;
  failed: number;
  products: number;
  publishedLabel: string;
  productsLabel: string;
}

/**
 * Anúncios publicados, com falha e produtos do catálogo do grupo. Empresa sem anúncios e sem produtos:
 * null (a linha não aparece).
 */
export function catalogLine(catalog: DashboardCatalog | null | undefined): CatalogLine | null {
  if (!catalog) return null;
  const published = catalog.adsPublished ?? 0;
  const failed = catalog.adsFailed ?? 0;
  const products = catalog.products ?? 0;
  if ((catalog.advertisements ?? 0) + published + failed + products === 0) return null;

  return {
    published,
    failed,
    products,
    publishedLabel: plural(published, 'anúncio publicado', 'anúncios publicados'),
    productsLabel: plural(products, 'produto no catálogo do grupo', 'produtos no catálogo do grupo'),
  };
}
